/**
 * Direct Messages Hooks
 * TanStack Query hooks for DM thread operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as dmService from '@/services/dm.service';
import type { DMThread } from '@/types/api.types';

/**
 * Query hook to list all DM threads for current user
 */
export const useDMThreads = () => {
  return useQuery({
    queryKey: ['dm-threads'],
    queryFn: dmService.listDMThreads,
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Query hook to get a single DM thread
 */
export const useDMThread = (threadId: string | null) => {
  return useQuery({
    queryKey: ['dm-threads', threadId],
    queryFn: () => dmService.getDMThread(threadId!),
    enabled: !!threadId,
  });
};

/**
 * Mutation hook to create or get existing DM thread
 */
export const useCreateDMThread = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userIds: string | string[]) => dmService.createDMThread(userIds),
    onSuccess: (newThread) => {
      // Add to threads list
      queryClient.setQueryData<DMThread[]>(['dm-threads'], (old) => {
        if (!old) return [newThread];
        // Check if already exists
        const exists = old.some((thread) => thread.id === newThread.id);
        if (exists) return old;
        return [newThread, ...old];
      });

      // Cache single thread
      queryClient.setQueryData(['dm-threads', newThread.id], newThread);
    },
  });
};

/**
 * Mutation hook to delete a DM thread
 */
export const useDeleteDMThread = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (threadId: string) => dmService.deleteDMThread(threadId),
    onMutate: async (threadId) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['dm-threads'] });

      // Snapshot previous value
      const previousThreads = queryClient.getQueryData<DMThread[]>(['dm-threads']);

      // Optimistically remove thread
      queryClient.setQueryData<DMThread[]>(['dm-threads'], (old) => {
        if (!old) return old;
        return old.filter((thread) => thread.id !== threadId);
      });

      return { previousThreads };
    },
    onError: (_err, _threadId, context) => {
      // Rollback on error
      if (context?.previousThreads) {
        queryClient.setQueryData(['dm-threads'], context.previousThreads);
      }
    },
    onSuccess: (_, threadId) => {
      // Remove single thread cache
      queryClient.removeQueries({ queryKey: ['dm-threads', threadId] });
      // Remove messages cache
      queryClient.removeQueries({ queryKey: ['messages', threadId] });
    },
  });
};

/**
 * Query hook to get list of users (for user picker)
 */
export const useUsers = () => {
  return useQuery({
    queryKey: ['users'],
    queryFn: dmService.listUsers,
    staleTime: 5 * 60 * 1000, // 5 minutes (users don't change often)
  });
};

/**
 * Mutation hook to mark a DM thread as read
 */
export const useMarkDMThreadAsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (threadId: string) => dmService.markDMThreadAsRead(threadId),
    onSuccess: (_, threadId) => {
      // Optimistically update thread's unread_count to 0
      queryClient.setQueryData<DMThread[]>(['dm-threads'], (old) => {
        if (!old) return [];
        return old.map((thread) =>
          thread.id === threadId
            ? { ...thread, unread_count: 0 }
            : thread
        );
      });
    },
  });
};
